import { create } from 'zustand';
import { RobustWebsocket } from '@/lib/api-clients/ws-client';
import {
  COLLATERAL_DECIMALS,
  CUM_FUNDING_DECIMALS,
  FUNDING_RATE_DECIMALS,
  MarketSpec,
  useMarketStore,
} from '@/store/use-markets-store';
import { parseDecimalToBigInt } from '@/utils/value-format';
import {
  FilledOrder,
  NewOrder,
  PartiallyFilledOrder,
  RejectedOrder,
  useOrdersStore,
} from '@/store/use-orders-store';
import {
  getAvailableFunds,
  getRealizedPnl,
  getSettledFunding,
} from '@/features/account/utils/math';
import {
  FilledTransfer,
  NewTransfer,
  ProcessingTransfer,
  RejectedTransfer,
  Transfer,
  VerifiedTransfer,
} from '@/store/use-transfers-store';
import {
  PrivateData,
  OrderStatus,
  DataMsg,
  ErrorCode,
  ErrorMsgs,
  CancelledEvent,
  RejectedEvent,
  WsType,
  AccountResponse,
} from '@/types';
import { useAccountStore } from './use-account-store';
import toastFilledOrder from '@/features/trade/order/components/toast/filled-order';
import { getIsStatusEval } from '@/features/vest-capital/utils/status';
import { toast } from 'sonner';
import { queryClient } from '@/lib/api-clients/query-client';
import { CapitalStatusResponse } from '@/lib/api/get-capital-status';
import { logConsole } from '@/lib/utils';
import { getAccountQueryOptions } from '@/features/account/api/get-account';
import { getByNormalizedKey } from '@/utils/helpers';
import {
  handleOrderForStore,
  handleTransferForStore,
} from '@/utils/handle-store-data';

type WebSocketStore = {
  publicWs: RobustWebsocket | null;
  privateWsByAddress: Record<string, RobustWebsocket>;
  privateConnectionsByAddress: Record<string, boolean>;
  isConnected: Record<WsType, boolean>;

  dependenciesBySymbol: Record<string, number>;
  increaseDependenciesForSymbol: (symbol: string) => void;
  decreaseDependenciesForSymbol: (symbol: string) => void;

  initializePublicWs: (url: string) => void;
  initializePrivateWs: (address: string, url: string) => void;
  disconnectWs: (
    type: WsType,
    reconnect?: boolean,
    options?: { address?: string },
  ) => void;
};

export const useWebSocketStore = create<WebSocketStore>((set, get) => ({
  publicWs: null,
  privateWsByAddress: {},
  privateConnectionsByAddress: {},
  isConnected: {
    [WsType.PUBLIC]: false,
    [WsType.PRIVATE]: false,
  },

  dependenciesBySymbol: {},
  increaseDependenciesForSymbol: (symbol) =>
    set((state) => ({
      dependenciesBySymbol: {
        ...state.dependenciesBySymbol,
        [symbol]: (state.dependenciesBySymbol[symbol] ?? 0) + 1,
      },
    })),
  decreaseDependenciesForSymbol: (symbol) =>
    set((state) => {
      const dependenciesBySymbol = { ...state.dependenciesBySymbol };
      if (!(symbol in dependenciesBySymbol)) return state;
      if (dependenciesBySymbol[symbol] <= 1) {
        delete dependenciesBySymbol[symbol];
      } else {
        dependenciesBySymbol[symbol]--;
      }
      return { dependenciesBySymbol };
    }),

  initializePublicWs: (url: string) => {
    const existingWs = get().publicWs;

    // Prevent multiple connections
    if (existingWs) {
      console.warn('Public WebSocket already initialized');
      return;
    }

    const ws = new RobustWebsocket({
      url,
      onConnected: () => {
        ws.addMessageHandler('ticker-handler', tickerMsgHandler);
        ws.addMessageHandler('depth-handler', depthMsgHandler);
        ws.addMessageHandler('trades-handler', tradesMsgHandler);

        set((state) => ({
          isConnected: { ...state.isConnected, [WsType.PUBLIC]: true },
        }));
      },
      onDisconnected: () =>
        set((state) => ({
          isConnected: { ...state.isConnected, [WsType.PUBLIC]: false },
        })),
    });

    ws.attachVisibilityHandler();
    set({ publicWs: ws });
    ws.safeConnect();
  },

  initializePrivateWs: (address: string, url: string) => {
    const normalizedAddress = address.toLowerCase();
    const existingWs = get().privateWsByAddress[normalizedAddress];

    // If WebSocket exists with different URL, disconnect first
    if (existingWs) {
      if (existingWs.url === url) {
        console.warn(
          `Private WebSocket already initialized for ${normalizedAddress}`,
        );
        return;
      }

      existingWs.detachVisibilityHandler();
      existingWs.disconnect(false);
    }

    const ws = new RobustWebsocket({
      url,
      onConnected: () => {
        const handlerKey = `order-handler-${normalizedAddress}`;

        ws.removeMessageHandler(handlerKey);
        ws.subscribeToChannels([`account_private`]);
        ws.addMessageHandler(handlerKey, (msg) =>
          orderMsgHandler(msg, address),
        );

        set((state) => {
          const privateConnectionsByAddress = {
            ...state.privateConnectionsByAddress,
            [normalizedAddress]: true,
          };
          const hasPrivateConnection = Object.values(
            privateConnectionsByAddress,
          ).some(Boolean);

          return {
            privateConnectionsByAddress,
            isConnected: {
              ...state.isConnected,
              [WsType.PRIVATE]: hasPrivateConnection,
            },
          };
        });
      },
      onDisconnected: () =>
        set((state) => {
          const privateConnectionsByAddress = {
            ...state.privateConnectionsByAddress,
            [normalizedAddress]: false,
          };
          const hasPrivateConnection = Object.values(
            privateConnectionsByAddress,
          ).some(Boolean);

          return {
            privateConnectionsByAddress,
            isConnected: {
              ...state.isConnected,
              [WsType.PRIVATE]: hasPrivateConnection,
            },
          };
        }),
    });

    ws.attachVisibilityHandler();
    set((state) => ({
      privateWsByAddress: {
        ...state.privateWsByAddress,
        [normalizedAddress]: ws,
      },
    }));
    ws.safeConnect();
  },

  disconnectWs: (type, reconnect = false, options) => {
    let wsName: keyof WebSocketStore;

    switch (type) {
      case WsType.PUBLIC:
        wsName = 'publicWs';
        break;
      case WsType.PRIVATE:
        wsName = 'privateWsByAddress';
        break;
    }

    if (wsName === 'privateWsByAddress') {
      const { privateWsByAddress } = get();
      const targetAddress = options?.address?.toLowerCase();
      const disconnectAddresses = targetAddress
        ? [targetAddress]
        : Object.keys(privateWsByAddress);

      disconnectAddresses.forEach((address) => {
        const ws = privateWsByAddress[address];
        if (!ws) return;
        ws.detachVisibilityHandler();
        ws.disconnect(reconnect);
      });

      set((state) => {
        const privateWsByAddress = { ...state.privateWsByAddress };
        const privateConnectionsByAddress = {
          ...state.privateConnectionsByAddress,
        };

        disconnectAddresses.forEach((address) => {
          delete privateWsByAddress[address];
          delete privateConnectionsByAddress[address];
        });

        const hasPrivateConnection = Object.values(
          privateConnectionsByAddress,
        ).some(Boolean);

        return {
          privateWsByAddress,
          privateConnectionsByAddress,
          isConnected: {
            ...state.isConnected,
            [WsType.PRIVATE]: hasPrivateConnection,
          },
        };
      });
      return;
    }

    const ws = get()[wsName] as RobustWebsocket | null;

    if (ws?.ws && ws.ws.readyState === WebSocket.OPEN) {
      ws.disconnect(reconnect);
    }
  },
}));

const handleTicker = (ticker: any, updatedAtMs?: number) => {
  const {
    symbol,
    oneHrFundingRate,
    cumFunding,
    imbalance, // unused for now
    indexPrice,
    markPrice,
    priceChange,
    priceChangePercent,
    status,
  } = ticker;

  const { setMarketData, marketData, marketSpec, setMarketSpec } =
    useMarketStore(symbol).getState();

  const midPrice =
    !marketData.midPrice && markPrice != null
      ? parseDecimalToBigInt(markPrice, marketSpec.priceDecimals)
      : null;

  setMarketData({
    markPrice: parseDecimalToBigInt(markPrice, marketSpec.priceDecimals),
    ...(midPrice !== null ? { midPrice } : {}),
    indexPrice: parseDecimalToBigInt(indexPrice, marketSpec.priceDecimals),
    oneHrFundingRate: parseDecimalToBigInt(
      oneHrFundingRate,
      FUNDING_RATE_DECIMALS,
    ),
    priceChange: parseDecimalToBigInt(priceChange, marketSpec.priceDecimals),
    priceChangePct: parseDecimalToBigInt(priceChangePercent, 4),
    cumFunding: parseDecimalToBigInt(cumFunding, CUM_FUNDING_DECIMALS),
    updatedAtMs: updatedAtMs ?? 0,
  });

  // TEMP
  // if (status) {
  //   setMarketSpec({ status });
  // }

  useAccountStore.getState().updatePosition(symbol);
};

const tickerMsgHandler = (msg: DataMsg) => {
  if (msg.channel === 'tickers') {
    msg.data.forEach((ticker: any) => handleTicker(ticker, msg.tsMs));
  } else if (msg.channel.endsWith('@ticker')) {
    handleTicker(msg.data, msg.tsMs); // assuming msg.data is already a single ticker object
  }

  return true;
};

const depthMsgHandler = (msg: DataMsg) => {
  if (!msg.channel) return;
  const [symbol, name] = msg.channel.split('@');
  if (name.startsWith('depth')) {
    const { setBook, setMarketData, marketData, marketSpec, activeTickSize } =
      useMarketStore(symbol).getState();

    const bids = msg.data.bids.map(([p, q]: [string, string]) => [
      parseDecimalToBigInt(p, marketSpec.priceDecimals),
      parseDecimalToBigInt(q, marketSpec.sizeDecimals),
    ]);
    const asks = msg.data.asks.map(([p, q]: [string, string]) => [
      parseDecimalToBigInt(p, marketSpec.priceDecimals),
      parseDecimalToBigInt(q, marketSpec.sizeDecimals),
    ]);

    const tickSize = name.includes('_') ? name.split('_').pop() : undefined;
    const book = { bids, asks };

    setBook(book, tickSize);

    let shouldUpdatePosition = false;

    const finestTickSize = marketSpec.tickSizes[0];
    if (tickSize ? tickSize === finestTickSize : true) {
      const bestAsk = asks[0]?.[0];
      const bestBid = bids[0]?.[0];
      if (bestAsk && bestBid && bestAsk > 0n && bestBid > 0n) {
        const midPrice = (bestAsk + bestBid) / 2n;
        if (midPrice !== marketData.midPrice) {
          setMarketData({ midPrice, midUpdatedMs: msg.tsMs ?? Date.now() });
          shouldUpdatePosition = true;
        }
      }
    }

    const effectiveTickSize = activeTickSize || marketSpec.defaultTickSize;

    if (tickSize && tickSize === effectiveTickSize) {
      setBook(book);
      shouldUpdatePosition = true;
    }

    if (!tickSize) {
      shouldUpdatePosition = true;
    }

    if (shouldUpdatePosition) {
      useAccountStore.getState().updatePosition(symbol);
    }

    return true;
  }
};

const tradesMsgHandler = (msg: DataMsg) => {
  if (msg.channel && msg.channel.endsWith('trades')) {
    const symbol = msg.channel.split('@')[0];
    const { time, ...trade } = msg.data;
    const { trades, setTrades } = useMarketStore(symbol).getState();
    setTrades([{ time: time, ...trade }, ...trades]);
  }
};

export const orderMsgHandler = (msg: DataMsg, walletAddress?: string) => {
  if (msg.channel && msg.channel === 'account_private') {
    const msgData: PrivateData = msg.data;
    const { event } = msgData;

    logConsole(false, '[Order Handler]: Message DATA', msgData);

    const { accounts, address: storeAddress } = useAccountStore.getState();

    const resolvedAddress = walletAddress ?? storeAddress;
    if (!resolvedAddress) return false;

    if (event === 'ORDER') {
      const {
        args: { nonce, ...args },
      } = msgData;

      const { setOrder } = useOrdersStore.getState();

      let marketSpec: MarketSpec;

      switch (args.status) {
        case OrderStatus.NEW:
          marketSpec = useMarketStore(args.symbol).getState().marketSpec;
          const newOrder: NewOrder = {
            ...args,
            limitPrice: parseDecimalToBigInt(
              args.limitPrice,
              marketSpec.priceDecimals,
            ),
            size: parseDecimalToBigInt(args.size, marketSpec.sizeDecimals),
            initMarginRatio: parseDecimalToBigInt(args.initMarginRatio, 4n),
            tpPrice: args.tpPrice
              ? parseDecimalToBigInt(args.tpPrice, marketSpec.priceDecimals)
              : undefined,
            slPrice: args.slPrice
              ? parseDecimalToBigInt(args.slPrice, marketSpec.priceDecimals)
              : undefined,
          };
          handleOrderForStore(newOrder, { address: resolvedAddress });
          return true;

        case OrderStatus.FILLED:
        case OrderStatus.PARTIALLY_FILLED:
          const account = getByNormalizedKey(accounts, resolvedAddress);
          if (!account) return false;
          const marketStore = useMarketStore(args.symbol).getState();
          marketSpec = marketStore.marketSpec;
          const isEval = getIsStatusEval(
            useAccountStore.getState().getCapitalStatus(resolvedAddress),
          );
          const parsedArgs = {
            ...args,
            limitPrice: parseDecimalToBigInt(
              args.limitPrice,
              marketSpec.priceDecimals,
            ),
            size: parseDecimalToBigInt(args.size, marketSpec.sizeDecimals),
            initMarginRatio: parseDecimalToBigInt(args.initMarginRatio, 4n),
            lastFilledSize: parseDecimalToBigInt(
              args.lastFilledSize,
              marketSpec.sizeDecimals,
            ),
            lastFilledPrice: parseDecimalToBigInt(
              args.lastFilledPrice,
              marketSpec.priceDecimals,
            ),
            avgFilledPrice: parseDecimalToBigInt(
              args.avgFilledPrice,
              marketSpec.priceDecimals,
            ),
            cumFunding: parseDecimalToBigInt(
              args.cumFunding,
              CUM_FUNDING_DECIMALS,
            ),
            fees: isEval
              ? 0n
              : parseDecimalToBigInt(args.fees, COLLATERAL_DECIMALS),
            markPrice: parseDecimalToBigInt(
              args.markPrice,
              marketSpec.priceDecimals,
            ),
          };
          const settledFunding = getSettledFunding(parsedArgs.symbol, account, {
            cumFunding: parsedArgs.cumFunding,
          });
          const realizedPnl = getRealizedPnl(
            parsedArgs.symbol,
            { ...parsedArgs, settledFunding: settledFunding.bigint },
            account.positions[parsedArgs.symbol],
          );
          const { cumFunding, ...rest } = parsedArgs;
          const filledOrder: FilledOrder | PartiallyFilledOrder = {
            ...rest,
            realizedPnl: realizedPnl.bigint,
            settledFunding: settledFunding.bigint,
            tpPrice: args.tpPrice
              ? parseDecimalToBigInt(args.tpPrice, marketSpec.priceDecimals)
              : undefined,
            slPrice: args.slPrice
              ? parseDecimalToBigInt(args.slPrice, marketSpec.priceDecimals)
              : undefined,
          };
          handleOrderForStore(filledOrder, {
            address: resolvedAddress,
            cumFunding,
          });
          toastFilledOrder(filledOrder, marketSpec);
          return true;

        case OrderStatus.CANCELLED:
          setOrder(args as CancelledEvent, resolvedAddress);
          const availableFunds = getAvailableFunds({
            address: resolvedAddress,
          });
          useAccountStore.getState().updateAccount({
            address: resolvedAddress,
            account: { availableFunds },
          });
          return true;

        case OrderStatus.REJECTED:
          const existingOrder = useOrdersStore
            .getState()
            .getOrders(resolvedAddress).orders[args.id];
          const rejectedSymbol = args.symbol || existingOrder?.symbol;

          if (rejectedSymbol) {
            marketSpec = useMarketStore(rejectedSymbol).getState().marketSpec;
            const rejectedOrder: RejectedOrder = {
              id: args.id,
              status: args.status,
              postTime: args.postTime ?? existingOrder?.postTime,
              code: args.code,
              symbol: rejectedSymbol,
              isBuy: args.isBuy ?? existingOrder?.isBuy,
              orderType: args.orderType ?? existingOrder?.orderType,
              reduceOnly: args.reduceOnly ?? existingOrder?.reduceOnly,
              limitPrice: args.limitPrice
                ? parseDecimalToBigInt(
                    args.limitPrice,
                    marketSpec.priceDecimals,
                  )
                : existingOrder?.limitPrice,
              size: args.size
                ? parseDecimalToBigInt(args.size, marketSpec.sizeDecimals)
                : existingOrder?.size,
              initMarginRatio: args.initMarginRatio
                ? parseDecimalToBigInt(args.initMarginRatio, 4n)
                : existingOrder?.initMarginRatio,
              tpPrice: args.tpPrice
                ? parseDecimalToBigInt(args.tpPrice, marketSpec.priceDecimals)
                : existingOrder?.tpPrice,
              slPrice: args.slPrice
                ? parseDecimalToBigInt(args.slPrice, marketSpec.priceDecimals)
                : existingOrder?.slPrice,
            };
            setOrder(rejectedOrder, resolvedAddress);
          } else {
            setOrder(
              {
                id: args.id,
                status: args.status,
                code: args.code,
                postTime: args.postTime ?? existingOrder?.postTime,
              } as RejectedEvent,
              resolvedAddress,
            );
          }
          return true;

        default:
          console.error('Unhandled order event: ', args);
          break;
      }
    } else if (event === 'TRANSFER') {
      const {
        args: { nonce, ...args },
      } = msgData;

      let transfer: Transfer;

      if (args.status === OrderStatus.REJECTED) {
        transfer = args as RejectedTransfer;
        toast.error(
          `Withdraw failed: ${ErrorMsgs[transfer.code as ErrorCode]}`,
        );
      } else {
        transfer = {
          ...args,
          size: parseDecimalToBigInt(args.size, COLLATERAL_DECIMALS),
        } as
          | FilledTransfer
          | NewTransfer
          | VerifiedTransfer
          | ProcessingTransfer;

        handleTransferForStore(transfer);

        return true;
      }
    } else if (event === 'FUNDING') {
      const {
        args: { status, crypto_amount, wallet_address },
      } = msgData;

      if (
        resolvedAddress &&
        wallet_address &&
        wallet_address !== resolvedAddress.toLowerCase()
      )
        return; // TEMP: prevent deposit on different account

      switch (status) {
        case 'completed':
          const depositEvent = new CustomEvent('app:deposit', {
            detail: { amount: crypto_amount },
          });
          window.dispatchEvent(depositEvent);
          break;
        case 'paid':
          toast.success(
            'Payment received! Your deposit will be processed shortly.',
          );
          break;
        case 'canceled':
          toast.error('Payment was canceled.');
          break;
        case 'failed':
          toast.error('Payment failed. Please try again.');
          break;
        case 'pending':
          const loading = toast.loading("We're processing your payment...");
          window.setTimeout(() => toast.dismiss(loading), 8000);
          break;
      }

      return true;
    } else if (event === 'ACCOUNT') {
      logConsole(false, 'WS account event received: ', msgData);

      const targetAddress = msgData.args.address ?? resolvedAddress;

      if (!targetAddress) {
        return true;
      }

      queryClient.setQueryData(
        getAccountQueryOptions(targetAddress).queryKey,
        (prev?: AccountResponse) => {
          if (!prev) return prev;
          const {
            args: {
              capital_status,
              target_equity,
              max_daily_loss_limit,
              max_drawdown_limit,
              prior_day_balance,
              prior_day_account_value,
            },
          } = msgData;
          const newAccount: AccountResponse = {
            ...prev,
            capital: {
              ...(prev.capital ?? {}),
              capitalStatus: capital_status ?? prev.capital?.capitalStatus,
              maxDailyLossLimit:
                max_daily_loss_limit ?? prev.capital?.maxDailyLossLimit,
              maxDrawdownLimit:
                max_drawdown_limit ?? prev.capital?.maxDrawdownLimit,
              targetEquity: target_equity ?? prev.capital?.targetEquity,
              priorDayBalance:
                prior_day_balance ?? prev.capital?.priorDayBalance,
              priorDayAccountValue:
                prior_day_account_value ?? prev.capital?.priorDayAccountValue,
            } as AccountResponse['capital'],
          };
          return newAccount;
        },
      );
      const { args } = msgData;

      queryClient.setQueriesData(
        {
          predicate: ({ queryKey }) => {
            const [key, addresses] = queryKey as readonly [string, string[]];
            if (key !== 'capital/status') return false;
            return Array.isArray(addresses) && addresses.includes(args.address);
          },
        },
        (prev?: CapitalStatusResponse) => {
          if (!prev) return { [args.address]: args };
          return { ...prev, [args.address]: args };
        },
      );

      return true;
    }
  }
};
